import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Paperclip } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { AreaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { get, mensagemDeErro, post, textoDeErroGravado } from '../../nucleo/api';
import { useContas } from './dados';
import { invalidarFinanceiro } from './tipos';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { textoLivre } from '../../nucleo/formato';

interface Job {
  estado: 'pendente' | 'rodando' | 'concluido' | 'erro';
  progresso: number;
  resultado: { extrato_id: string | null; total: number; importadas: number; ignoradas: number; erros: { linha: number; motivo: string }[] } | null;
  erro: string | null;
}

/** M4.4: importa OFX ou CSV por job. Reimportar o mesmo arquivo não duplica (id do banco único por conta). */
export function ImportarExtrato({ aberto, contaInicial, aoFechar }: { aberto: boolean; contaInicial?: string; aoFechar: () => void }) {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const contas = useContas(true);
  const [conta, setConta] = useState(contaInicial ?? '');
  const [formato, setFormato] = useState<'ofx' | 'csv'>('ofx');
  const [conteudo, setConteudo] = useState('');
  const [nome, setNome] = useState('');
  const seletor = useRef<HTMLInputElement>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(null, erro);
  const [enviando, setEnviando] = useState(false);
  const contaEscolhida = conta || contaInicial || contas.data?.itens.find((c) => c.padrao)?.id || '';
  const job = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => get<Job>(`/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data && ['concluido', 'erro'].includes(q.state.data.estado) ? false : 1000),
  });
  const pronto = job.data?.estado === 'concluido';
  useEffect(() => {
    if (pronto) void invalidarFinanceiro(qc);
  }, [pronto, qc]);
  const fechar = () => {
    setConteudo('');
    setNome('');
    setJobId(null);
    setErro(null);
    aoFechar();
  };
  async function escolherArquivo(f: File | undefined) {
    if (!f) return;
    setNome(f.name);
    setFormato(/\.csv$/i.test(f.name) ? 'csv' : 'ofx');
    setConteudo(await f.text());
  }
  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={t('importar.titulo')}
      largura="lg"
      rodape={
        <>
          <Botao variante="fantasma" onClick={fechar}>
            {jobId ? tn('comum.fechar') : tn('comum.cancelar')}
          </Botao>
          {!jobId && (
            <Botao
              variante="primario"
              carregando={enviando}
              disabled={!conteudo.trim() || !contaEscolhida}
              onClick={async () => {
                setEnviando(true);
                setErro(null);
                try {
                  setJobId((await post<{ job_id: string }>('/financeiro/extratos/importar', { conta_id: contaEscolhida, formato, conteudo, nome: nome || undefined })).job_id);
                } catch (e) {
                  setErro(mensagemDeErro(e));
                } finally {
                  setEnviando(false);
                }
              }}
            >
              {t('importar.enviar')}
            </Botao>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-2">{t('importar.texto')}</p>
        {erro && (
          <p role="alert" className="text-sm text-perigo">
            {erro}
          </p>
        )}
        {!jobId ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Rotulo id="imp-conta" rotulo={t('campos.conta')} obrigatorio>
                <Selecao id="imp-conta" opcoes={(contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))} value={contaEscolhida} onChange={(e) => setConta(e.target.value)} />
              </Rotulo>
              <Rotulo id="imp-formato" rotulo={t('importar.formato')}>
                <Selecao
                  id="imp-formato"
                  opcoes={[
                    { valor: 'ofx', rotulo: 'OFX' },
                    { valor: 'csv', rotulo: 'CSV' },
                  ]}
                  value={formato}
                  onChange={(e) => setFormato(e.target.value as 'ofx' | 'csv')}
                />
              </Rotulo>
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <span className="text-sm text-tinta-2">{t('importar.arquivo')}</span>
              <Botao icone={<Paperclip className="size-4" />} onClick={() => seletor.current?.click()}>
                {nome ? t('acoes.trocarArquivo') : t('importar.escolherArquivo')}
              </Botao>
              {nome && <span className="min-w-0 truncate text-sm text-tinta-2" title={nome}>{nome}</span>}
              <input ref={seletor} type="file" accept=".ofx,.csv,.txt,text/csv" className="sr-only" tabIndex={-1} aria-label={t('importar.arquivo')} title={t('importar.arquivo')} onChange={(e) => void escolherArquivo(e.target.files?.[0])} />
            </div>
            <AreaSensivel
              aria-label={t('importar.conteudo')}
              rows={9}
              value={conteudo}
              onChange={(e) => setConteudo(e.target.value)}
              placeholder={t('importar.exemplo')}
              className="numero w-full rounded-lg border border-borda-campo bg-campo p-3 text-xs placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
            />
          </>
        ) : (
          <div role="status" className="rounded-lg border border-borda p-4">
            {!pronto && job.data?.estado !== 'erro' && (
              <>
                <p className="text-sm">{t(job.data?.estado === 'pendente' ? 'importar.naFila' : 'importar.andamento')}</p>
                <div className="mt-2 h-2 w-full rounded-full bg-sup-3">
                  <div className={`h-2 rounded-full bg-acento-forte transition-all ${job.data?.estado === 'pendente' ? 'animate-pulse' : ''}`} style={{ width: `${job.data?.estado === 'pendente' ? 5 : (job.data?.progresso ?? 5)}%` }} />
                </div>
              </>
            )}
            {job.data?.estado === 'erro' && <p className="text-sm text-perigo">{textoDeErroGravado(job.data.erro)}</p>}
            {pronto && job.data?.resultado && (
              <>
                <p className="text-sm font-medium">{t('importar.resultado', { importadas: job.data.resultado.importadas, ignoradas: job.data.resultado.ignoradas, erros: job.data.resultado.erros.length })}</p>
                <ul className="mt-2 max-h-40 overflow-y-auto text-xs text-tinta-3">
                  {job.data.resultado.erros.map((e, i) => (
                    <li key={i}>
                      {e.linha > 0 && <span className="numero">#{e.linha} </span>}
                      {textoLivre(e.motivo)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
